import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, ChevronDown, X as XIcon, Search } from "lucide-react";

/**
 * PartsMultiSelect — searchable checkbox dropdown for picking repair-parts on a quote.
 *   - Built-in search input filters the option list as you type (case-insensitive substring).
 *   - Selected parts are pinned to the top of the list (a divider separates them from the rest).
 *   - Admins can inline-create a new part without leaving the form.
 */
export function PartsMultiSelect({
  options,
  selected,
  onChange,
  isAdmin = false,
  activeCategory = "",
  onCreate = null,
}) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Focus the search box when the menu opens
    window.setTimeout(() => searchRef.current?.focus(), 50);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setAdding(false);
        setNewName("");
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = (name) => {
    const cur = new Set(selected || []);
    if (cur.has(name)) cur.delete(name);
    else cur.add(name);
    onChange(Array.from(cur).sort());
  };

  // Filter + split into selected (top) and unselected (bottom). Within each group,
  // results stay in the option order (already alphabetical from the backend).
  const { selectedRows, otherRows } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selSet = new Set(selected || []);
    const filtered = q ? options.filter((p) => p.name.toLowerCase().includes(q)) : options;
    return {
      selectedRows: filtered.filter((p) => selSet.has(p.name)),
      otherRows: filtered.filter((p) => !selSet.has(p.name)),
    };
  }, [options, selected, query]);

  const createPart = async () => {
    const name = newName.trim();
    if (!name || !onCreate) return;
    setCreating(true);
    try {
      await onCreate(name);
      setNewName("");
      setAdding(false);
    } finally {
      setCreating(false);
    }
  };

  const summary = (selected || []).length === 0
    ? "Select parts…"
    : `${selected.length} part${selected.length === 1 ? "" : "s"} selected`;

  const Row = ({ p, pinned }) => {
    const on = pinned;
    return (
      <label
        key={p.id}
        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
        data-testid={`part-option-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <input
          type="checkbox"
          checked={on}
          onChange={() => toggle(p.name)}
          className="h-4 w-4 accent-brand-blue"
        />
        <span className={on ? "font-semibold text-slate-900" : "text-slate-700"}>{p.name}</span>
      </label>
    );
  };

  return (
    <div className="relative" ref={ref} data-testid="parts-multiselect">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="parts-multiselect-trigger"
        className="qb-input flex items-center justify-between gap-2 text-left"
      >
        <span className={`truncate ${(selected || []).length === 0 ? "text-slate-400" : ""}`}>{summary}</span>
        <ChevronDown size={14} className={`flex-shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 min-w-[300px] max-h-80 overflow-hidden flex flex-col bg-white border border-slate-300 rounded-md shadow-lg"
          data-testid="parts-multiselect-menu"
        >
          {/* Sticky search box */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search parts…"
                className="qb-input h-8 pl-7 text-sm"
                data-testid="parts-search-input"
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setQuery(""); setOpen(false); }
                }}
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-2">
            {options.length === 0 && !adding && (
              <div className="text-xs text-slate-500 px-2 py-3">
                No parts configured{activeCategory ? ` for ${activeCategory}` : ""} yet.
                {onCreate && <span className="block mt-1 text-slate-400">Click "Add new part" below to create one.</span>}
              </div>
            )}

            {/* Selected — pinned at top */}
            {selectedRows.length > 0 && (
              <>
                <div className="px-2 pt-1 pb-1 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                  Selected ({selectedRows.length})
                </div>
                {selectedRows.map((p) => <Row key={p.id} p={p} pinned />)}
                {otherRows.length > 0 && <div className="border-t border-slate-100 my-1" />}
              </>
            )}

            {/* Remaining options */}
            {otherRows.length > 0 && selectedRows.length > 0 && (
              <div className="px-2 pt-1 pb-1 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                Other parts
              </div>
            )}
            {otherRows.map((p) => <Row key={p.id} p={p} pinned={false} />)}

            {query && selectedRows.length === 0 && otherRows.length === 0 && options.length > 0 && (
              <div className="text-xs text-slate-500 px-2 py-3">No parts match "{query}".</div>
            )}
          </div>

          {/* Any authenticated user can add a missing repair part inline. */}
          {onCreate && (
            <div className="border-t border-slate-100 p-1">
              {!adding ? (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-brand-blue hover:bg-blue-50 font-semibold"
                  data-testid="parts-add-new-button"
                >
                  <Plus size={14} /> Add new part{activeCategory ? ` to ${activeCategory}` : ""}
                </button>
              ) : (
                <div className="flex items-center gap-1 px-1 py-1">
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); createPart(); }
                      if (e.key === "Escape") { setAdding(false); setNewName(""); }
                    }}
                    placeholder="New part name"
                    className="qb-input h-8 text-sm flex-1"
                    data-testid="parts-add-new-input"
                  />
                  <button
                    type="button"
                    onClick={createPart}
                    disabled={creating || !newName.trim()}
                    className="h-8 px-2 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold rounded disabled:opacity-50"
                    data-testid="parts-add-new-save"
                  >
                    {creating ? "…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAdding(false); setNewName(""); }}
                    className="h-8 px-2 text-slate-500 hover:text-slate-900"
                    aria-label="Cancel"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              )}
            </div>
          )}

          {(selected || []).length > 0 && (
            <div className="border-t border-slate-100 px-2 py-1 flex items-center justify-between text-[10px] uppercase tracking-wider">
              <button type="button" onClick={() => onChange([])} className="text-slate-500 hover:text-red-600 font-semibold" data-testid="parts-clear-all">
                Clear all
              </button>
              <button type="button" onClick={() => setOpen(false)} className="text-brand-blue hover:underline font-semibold">Done</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
